import * as React from "react"
import { ThemeCss, ProgressBar, Tooltip, Theme } from "gatsby-interface"
import { PlatformLimitSummary, PlatformUsage } from "@modules/graphql/types"
import { MdReportProblem } from "react-icons/md"
import { imageCdnIntegrationConfig as text } from "@modules/locales/default.js"

const OVERAGE_WARNING_THRESHOLD = 75

const colCss: ThemeCss = theme => ({
  display: `grid`,
  rowGap: theme.space[2],
  alignContent: `start`,
})

const labelCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[50],
  textTransform: `uppercase`,
  fontWeight: theme.fontWeights.body,
})

const descriptionCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[50],
})

const progressBarCss: ThemeCss = theme => ({
  marginTop: `0.4rem`, // manually set value to align with sibling widgets,
  marginBottom: theme.space[2],

  [theme.mediaQueries.desktop]: {
    width: `100px`,
  },
})

const tooltipCss: ThemeCss = theme => ({
  paddingBottom: theme.space[1],
  fontSize: theme.fontSizes[1],

  p: {
    margin: 0,
    whiteSpace: `nowrap`,
  },

  svg: {
    color: theme.colors.orange[60],
    verticalAlign: `middle`,
  },
})

type TooltipContentProps = {
  name: string
  workspace?: PlatformUsage | null
  site?: PlatformUsage | null
}

function TooltipContent({ name, workspace, site }: TooltipContentProps) {
  if (!workspace || !site) {
    return null
  }

  return (
    <div css={tooltipCss}>
      <p>
        This site:{" "}
        {site.percentageOfLimit >= OVERAGE_WARNING_THRESHOLD && (
          <MdReportProblem />
        )}{" "}
        <strong>{site.formattedCount}</strong> {name.toLowerCase()}
      </p>
      <p>
        This workspace:{" "}
        {workspace.percentageOfLimit >= OVERAGE_WARNING_THRESHOLD && (
          <MdReportProblem />
        )}{" "}
        <strong>{workspace.formattedCount}</strong> {name.toLowerCase()}
      </p>
    </div>
  )
}

const getProgressColor = (theme: Theme, progression: number) => {
  if (progression >= OVERAGE_WARNING_THRESHOLD) {
    return theme.colors.orange[60]
  }

  return theme.colors.purple[60]
}

const getSecondProgressColor = (theme: Theme, progression: number) => {
  if (progression >= OVERAGE_WARNING_THRESHOLD) {
    return theme.colors.orange[40]
  }

  return theme.colors.grey[30]
}

export type ImageCdnUsageProps = {
  imageCdnUsageSummary?: PlatformLimitSummary
}

export function ImageCdnUsage({ imageCdnUsageSummary }: ImageCdnUsageProps) {
  return imageCdnUsageSummary?.site ? (
    <Tooltip
      key={imageCdnUsageSummary.name}
      label={
        <TooltipContent
          workspace={imageCdnUsageSummary.workspace}
          site={imageCdnUsageSummary.site}
          name={text.messages.tooltipLabel}
        />
      }
    >
      <div css={colCss} key={imageCdnUsageSummary.name}>
        <span css={labelCss}>{text.labels.usageHeaderText}</span>
        <div css={progressBarCss}>
          <ProgressBar
            value={imageCdnUsageSummary.site?.percentageOfLimit || 0}
            secondValue={imageCdnUsageSummary.workspace?.percentageOfLimit || 0}
            max={100}
            aria-describedby={imageCdnUsageSummary.name}
            height={8}
            getProgressColor={getProgressColor}
            getSecondProgressColor={getSecondProgressColor}
          />
        </div>
        <span
          id={imageCdnUsageSummary.name}
          css={descriptionCss}
        >{`${imageCdnUsageSummary.site?.formattedCount} / ${imageCdnUsageSummary.formattedLimit}`}</span>
      </div>
    </Tooltip>
  ) : null
}
